import { authState } from '../auth.js';
import { SYSTEMS } from '../cores/systems.js';
import { systemTag } from '../../shared/system-label.js';
import { moduleRegistry } from '../module-registry.js';
import { roomScreenshotSrc } from '../room-thumbs.js';
import { TOP_GAMES, TOP_GAMES_LABELS } from '../data/top-games.js';
import { renderMenuFriendsColumn, renderMenuSuggestedCard } from '../friends.js';
import { notifyNavigation } from './screen-router.js';

// Game-title fragment for an activity-card sentence: a real launch
// <button> when the feed item carries a launchable ROM (rom_filename,
// resolved server-side), plain escaped text otherwise. Rows are built
// as innerHTML strings, so the click handler is wired after they land
// in the DOM — see the .friend-activity-game querySelectorAll below.
function activityGameHtml(app, item) {
  const title = item.game_title || '';
  if (!title) return '';
  if (!item.rom_filename) return app._escapeHtml(title);
  const sys = item.rom_system || item.system_id || '';
  return `<button type="button" class="friend-activity-game activity-game-launch"` +
    ` data-rom="${app._escapeHtml(item.rom_filename)}" data-system="${app._escapeHtml(sys)}"` +
    ` aria-label="Play ${app._escapeHtml(title)}">${app._escapeHtml(title)}</button>`;
}

export async function loadActivityCard(app) {
  const card = document.getElementById('friends-activity-card');
  const list = document.getElementById('friends-activity-list');
  const tabContainer = document.getElementById('activity-widget-tabs');
  if (!card || !list) return;
  if (!authState.isLoaded()) {
    try { await authState.refreshSession(); } catch { /* non-fatal */ }
  }
  const user = authState.getCurrentUser();
  if (!user) { card.classList.add('hidden'); return; }

  let activeWidgetTab = 'all';
  let cardVisible = false;

  const loadTab = async (tab) => {
    activeWidgetTab = tab;
    if (tabContainer) tabContainer.querySelectorAll('.activity-widget-tab').forEach(btn => btn.classList.toggle('active', btn.dataset.tab === tab));
    const viewAll = document.getElementById('friends-activity-view-all');
    if (viewAll) { viewAll.href = tab === 'friends' ? '/friends/activity' : '/activity'; viewAll.onclick = (e) => { e.preventDefault(); app._navigateToActivity(null, tab); }; }

    let url;
    if (tab === 'all') url = '/api/activity/all?limit=10';
    else if (tab === 'friends') url = '/api/friends/activity?limit=10';
    else url = `/api/users/${encodeURIComponent(user.username)}/activity?limit=10`;

    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) { if (!cardVisible) card.classList.add('hidden'); return; }
      const { items } = await res.json();

      if (!items || !items.length) {
        if (!cardVisible) { card.classList.add('hidden'); return; }
        list.innerHTML = `<div class="card-empty-msg">${tab === 'friends' ? 'No friend activity yet' : 'No activity yet'}</div>`;
        return;
      }

      cardVisible = true;
      card.classList.remove('hidden');
      list.innerHTML = '';
      for (const item of items) {
        const row = document.createElement('div');
        row.className = 'friend-activity-row';

        const uname = item.username || user.username;
        const displayName = item.display_username || user.displayUsername || user.username;
        const uimage = item.user_image || `/api/users/${encodeURIComponent(uname)}/avatar`;

        let text;
        const isClip = item.type === 'clip';
        const isReplay = item.type === 'replay';
        if (item.type === 'friendship') {
          text = `became friends with ${app._escapeHtml(item.friend_display_username || item.friend_username || 'someone')}`;
        } else if (isClip) {
          const g = activityGameHtml(app, item);
          text = item.trigger_type === 'achievement'
            ? `clipped ${app._escapeHtml(item.trigger_ref || 'an achievement')} in ${g}`
            : item.trigger_type === 'high_score' ? `clipped a high score in ${g}`
            : `clipped ${g || 'a moment'}`;
        } else if (isReplay) {
          const g = activityGameHtml(app, item);
          const cat = item.category;
          text = cat === 'achievement'
            ? `recorded a run earning ${app._escapeHtml(item.category_ref || 'an achievement')} in ${g}`
            : cat === 'high_score' ? `recorded a high-score run in ${g}`
            : cat === 'speedrun' ? `submitted a speedrun of ${g}`
            : `saved a replay of ${g || 'a game'}`;
        } else if (item.type === 'achievement') {
          text = `unlocked ${app._escapeHtml(item.achievement_icon || '\u{1F3C6}')} ${app._escapeHtml(item.achievement_name)} in ${activityGameHtml(app, item)}`;
        } else if (item.type === 'leaderboard') {
          const typeLabel = item.leaderboard_type === 'global_points' ? 'Global Points'
            : item.leaderboard_type === 'game_achievements' ? 'Achievements'
            : item.leaderboard_type === 'game_scores' ? 'High Score' : 'Leaderboard';
          text = item.game_title
            ? `reached #${item.leaderboard_rank} in ${typeLabel} · ${activityGameHtml(app, item)}`
            : `reached #${item.leaderboard_rank} in ${typeLabel}`;
        } else {
          const isNetplay = item.net_mode === 'host' || item.net_mode === 'guest'
            || item.net_mode === 'lockstep';
          text = isNetplay
            ? `played ${activityGameHtml(app, item) || 'a game'} (netplay)`
            : `played ${activityGameHtml(app, item) || 'a game'}`;
        }

        const ago = app._formatRelativeShort(new Date(item.ts).getTime());

        let leftHtml = '';
        if (isClip) {
          row.className = 'friend-activity-row friend-activity-row-clip';
          const cid = app._escapeHtml(item.clip_id);
          leftHtml = `<div class="friend-activity-clip-thumb" data-clip-id="${cid}"><img src="/api/clips/${cid}/thumbnail" alt="" loading="lazy" onerror="this.style.display='none'"><span class="friend-activity-clip-play">▶</span></div>`;
        } else if (isReplay) {
          // Reuse the clip-thumb visual treatment so the feed stays
          // visually consistent. Distinct data-attr drives the click target.
          row.className = 'friend-activity-row friend-activity-row-clip';
          const rid = app._escapeHtml(item.replay_id);
          leftHtml = `<div class="friend-activity-clip-thumb" data-replay-id="${rid}"><img src="/api/replays/${rid}/thumbnail" alt="" loading="lazy" onerror="this.style.display='none'"><span class="friend-activity-clip-play">📼</span></div>`;
        } else {
          leftHtml = `<retro-avatar class="friend-activity-avatar" username="${app._escapeHtml(uname)}" size="list" fallback-avatar></retro-avatar>`;
        }

        const nameHtml = uname
          ? `<span class="friend-activity-name" data-username="${app._escapeHtml(uname)}">${app._escapeHtml(displayName)}</span>`
          : '';

        row.innerHTML = `${leftHtml}${nameHtml}<span class="friend-activity-text">${text}</span><span class="friend-activity-time">${ago}</span>`;
        // Tooltip carries the plain sentence. Assigned as a property from
        // textContent because `text` may contain launch-<button> markup,
        // which can't be baked into a title="" attribute.
        const textEl = row.querySelector('.friend-activity-text');
        textEl.title = uname ? `${displayName} ${textEl.textContent}` : textEl.textContent;
        list.appendChild(row);
      }

      list.querySelectorAll('.friend-activity-clip-thumb').forEach(el => {
        el.addEventListener('click', () => {
          const clipId = el.dataset.clipId;
          const replayId = el.dataset.replayId;
          if (clipId) app._openClipViewer(clipId);
          else if (replayId) app._openReplayViewer(replayId);
        });
      });

      list.querySelectorAll('.friend-activity-name').forEach(el => {
        el.addEventListener('click', () => {
          const u = el.dataset.username;
          if (u) app._navigateToStats(u);
        });
      });

      list.querySelectorAll('.friend-activity-game').forEach(el => {
        el.addEventListener('click', () => {
          app._launchTopGame(el.dataset.system, {
            title: el.textContent,
            rom_filename: el.dataset.rom,
          });
        });
      });

    } catch {
      if (!cardVisible) card.classList.add('hidden');
    }
  };

  if (tabContainer) {
    tabContainer.querySelectorAll('.activity-widget-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        const t = btn.dataset.tab;
        if (t && t !== activeWidgetTab) loadTab(t);
      });
    });
  }

  loadTab('all');
}

export function formatRelativeShort(app, ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

// ── Friends column (homepage sidebar) ──────────────────────────
export function loadFriendsColumn(app) {
  renderMenuFriendsColumn({
    navigateToProfile: (u) => app._navigateToStats(u),
    navigateToFriends: () => app._navigateToFriends(),
  });
}

export function loadSuggestedCard(app) {
  renderMenuSuggestedCard({
    navigateToProfile: (u) => app._navigateToStats(u),
  });
}

// ── Rooms card (homepage widget) ────────────────────────────────

export async function loadActiveGamesCard(app) {
  const card = document.getElementById('active-games-card');
  const list = document.getElementById('active-games-list');
  const viewAll = document.getElementById('active-games-view-all');
  if (!card || !list) return;

  try {
    const res = await fetch('/api/rooms/browse', { cache: 'no-store' });
    if (!res.ok) throw new Error();
    const data = await res.json();
    const allRooms = data.rooms || [];

    list.innerHTML = '';
    if (!allRooms.length) {
      card.classList.add('hidden');
      return;
    }

    card.classList.remove('hidden');
    const shown = allRooms.slice(0, 5);
    for (const room of shown) {
      const watchMultitap = room.roomProfile === 'snes-multitap-v1' && room.watchable === true;
      const entry = document.createElement('div');
      entry.className = 'active-game-entry';

      let thumbHtml;
      if (room.hasScreenshot) {
        thumbHtml = `<img class="active-game-thumb" src="${roomScreenshotSrc(room.code)}" data-screenshot-room="${app._escapeHtml(room.code)}" alt="">`;
      } else if (room.gameId) {
        thumbHtml = `<img class="active-game-thumb" src="/api/library/artwork/cover/${room.gameId}" alt="">`;
      } else {
        thumbHtml = `<div class="active-game-thumb-placeholder">${app._systemEmoji(room.system)}</div>`;
      }

      const avatarHtml = room.hostUsername
        ? `<retro-avatar class="active-game-avatar" username="${app._escapeHtml(room.hostUsername)}" size="list" fallback-avatar></retro-avatar>`
        : '';

      const typeBadge = room.type === 'matchmaking'
        ? '<span class="active-game-type-badge matchmaking">JOIN</span>'
        : '';

      const viewers = room.type === 'solo' || watchMultitap
        ? `<span class="active-game-viewers">${room.spectatorCount > 0 ? `\u{1F441} ${room.spectatorCount}` : 'Watch Live'}</span>`
        : '';

      // Render the system tag in two spots sharing the base style: the compact
      // right-aligned one (default) and an inline one inside the info column. CSS
      // toggles which shows — at iPad-landscape and above the inline tag takes its
      // own row (before the viewers line) so it never truncates the game title.
      const systemLabel = app._escapeHtml(systemTag(room.system));

      entry.innerHTML =
        `${thumbHtml}` +
        `${avatarHtml}` +
        `<div class="active-game-info">` +
          `<span class="active-game-title">${app._escapeHtml(room.romTitle || room.romName || 'Unknown')}</span>` +
          `<span class="active-game-host">${app._escapeHtml(room.hostDisplayUsername || room.hostUsername || 'Anonymous')}</span>` +
          `<span class="active-game-system active-game-system--inline">${systemLabel}</span>` +
          viewers +
        `</div>` +
        typeBadge +
        `<span class="active-game-system active-game-system--side">${systemLabel}</span>`;

      const coverImg = room.gameId && !room.hasScreenshot ? entry.querySelector('.active-game-thumb') : null;
      if (coverImg) {
        coverImg.onerror = () => { coverImg.outerHTML = `<div class="active-game-thumb-placeholder">${app._systemEmoji(room.system)}</div>`; };
      }

      // A cross-replica thumbnail can momentarily 404 (owner slow / frame not
      // pulled yet). Fall back to cover art, else the placeholder — and drop the
      // marker so the animator stops re-fetching the failing screenshot.
      const shotImg = room.hasScreenshot ? entry.querySelector('.active-game-thumb[data-screenshot-room]') : null;
      if (shotImg) {
        shotImg.onerror = () => {
          if (room.gameId) {
            shotImg.removeAttribute('data-screenshot-room');
            shotImg.onerror = () => { shotImg.outerHTML = `<div class="active-game-thumb-placeholder">${app._systemEmoji(room.system)}</div>`; };
            shotImg.src = `/api/library/artwork/cover/${room.gameId}`;
          } else {
            shotImg.outerHTML = `<div class="active-game-thumb-placeholder">${app._systemEmoji(room.system)}</div>`;
          }
        };
      }

      entry.addEventListener('click', () => {
        if (watchMultitap) {
          app._joinRoom(room.code, null, { spectator: true });
        } else if (room.type === 'matchmaking' || room.type === 'live') {
          app._joinRoom(room.code);
        } else if (room.hostUsername) {
          window.history.pushState({}, '', `/u/${encodeURIComponent(room.hostUsername)}/play`);
          notifyNavigation();
          app._joinWatchRoom(room.hostUsername);
        }
      });
      list.appendChild(entry);
    }

    if (viewAll) {
      viewAll.classList.toggle('hidden', data.total <= 5);
      viewAll.addEventListener('click', (e) => {
        e.preventDefault();
        app._navigateToRooms();
      }, { once: true });
    }
  } catch {
    card.classList.add('hidden');
  }
}

export function systemEmoji(app, sys) {
  const map = { nes: '🎮', snes: '🕹️', gb: '🎮', gbc: '🎮', gba: '🎮', genesis: '🎮', atari: '👾', c64: '💾' };
  return map[sys] || '🎮';
}

// ── TRENDING GAMES card ────────────────────────────────────────────

export function systemTabList(app) {
  const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname.endsWith('.trycloudflare.com');
  return Object.keys(SYSTEMS).filter(id =>
    SYSTEMS[id].id === id && (isLocal || moduleRegistry.isEnabled(`system:${id}`))
  );
}

export const TAB_LABELS = { gb: 'GAME BOY', atari2600: 'ATARI' };

export async function loadTrendingCard(app) {
  const card = document.getElementById('trending-card');
  const tabsEl = document.getElementById('trending-tabs');
  const list = document.getElementById('trending-list');
  if (!card || !tabsEl || !list) return;

  app._trendingCache = app._trendingCache || {};

  const systems = app._systemTabList();
  const defaultSys = app.system?.id || 'nes';

  tabsEl.innerHTML = '';
  for (const sys of systems) {
    const tab = document.createElement('button');
    tab.className = `top-games-tab${sys === defaultSys ? ' active' : ''}`;
    tab.textContent = TAB_LABELS[sys] || sys.toUpperCase();
    tab.type = 'button';
    tab.addEventListener('click', () => {
      tabsEl.querySelectorAll('.top-games-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      app._renderTrendingGames(sys, list);
    });
    tabsEl.appendChild(tab);
  }

  await app._renderTrendingGames(defaultSys, list);
  if ((app._trendingCache[defaultSys] || []).length > 0) {
    card.classList.remove('hidden');
  }
}

export async function renderTrendingGames(app, system, list) {
  if (app._trendingCache[system]) {
    app._renderTrendingList(app._trendingCache[system], list, system);
    return;
  }
  try {
    const res = await fetch(`/api/trending/games?system=${encodeURIComponent(system)}&limit=8`);
    if (!res.ok) throw new Error();
    const data = await res.json();
    app._trendingCache[system] = data.games || [];
    app._renderTrendingList(app._trendingCache[system], list, system);
  } catch {
    app._trendingCache[system] = [];
  }
}

export function renderTrendingList(app, games, list, system) {
  list.innerHTML = '';
  if (!games.length) return;
  for (const g of games) {
    const entry = document.createElement('div');
    entry.className = 'top-game-entry';
    const playTime = app._formatDuration(g.total_play_seconds);
    entry.innerHTML =
      `<span class="top-game-rank">${games.indexOf(g) + 1}</span>` +
      `<span class="top-game-title" title="${app._escapeHtml(g.title || 'Unknown')}">${app._escapeHtml(g.title || 'Unknown')}</span>` +
      `<span class="top-game-stats">${g.unique_players} player${g.unique_players !== 1 ? 's' : ''} &middot; ${playTime}</span>`;
    if (g.rom_filename) {
      entry.addEventListener('click', () => app._launchTopGame(g.rom_system || system, g));
    }
    list.appendChild(entry);
  }
}

// ── TOP 10 PICKS card ─────────────────────────────────────────────

export function loadTopPicksCard(app) {
  const card = document.getElementById('top-picks-card');
  const tabsEl = document.getElementById('top-picks-tabs');
  const list = document.getElementById('top-picks-list');
  if (!card || !tabsEl || !list) return;

  const keys = Object.keys(TOP_GAMES);
  const sysId = app.system?.id;
  const defaultKey = TOP_GAMES[sysId] ? sysId
    : (sysId === 'gb' || sysId === 'gbc') ? 'gameboy'
    : 'nes';

  tabsEl.innerHTML = '';
  for (const key of keys) {
    const tab = document.createElement('button');
    tab.className = `top-games-tab${key === defaultKey ? ' active' : ''}`;
    tab.textContent = TOP_GAMES_LABELS[key] || key.toUpperCase();
    tab.type = 'button';
    tab.addEventListener('click', () => {
      tabsEl.querySelectorAll('.top-games-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      app._renderTopPicksList(key, list);
    });
    tabsEl.appendChild(tab);
  }

  app._renderTopPicksList(defaultKey, list);
}

export function renderTopPicksList(app, key, list) {
  const games = TOP_GAMES[key] || [];
  list.innerHTML = '';
  for (let i = 0; i < games.length; i++) {
    const g = games[i];
    const launchSystem = g.system || key;
    const entry = document.createElement('div');
    entry.className = 'top-game-entry';
    entry.innerHTML =
      `<span class="top-game-rank">${i + 1}</span>` +
      `<span class="top-game-title" title="${app._escapeHtml(g.title)}">${app._escapeHtml(g.title)}</span>`;
    entry.addEventListener('click', () => {
      app._launchTopGame(launchSystem, { title: g.title, rom_filename: g.rom });
    });
    list.appendChild(entry);
  }

  const viewAll = document.createElement('a');
  viewAll.className = 'card-view-all';
  viewAll.href = '/top100';
  viewAll.textContent = `${TOP_GAMES_LABELS[key] || key.toUpperCase()} Top 100 →`;
  viewAll.addEventListener('click', (e) => {
    e.preventDefault();
    app._navigateToTop100(key);
  });
  list.appendChild(viewAll);
}

// ── TOP GAMES card ────────────────────────────────────────────────

// NOTE: _topGamesCache is an instance field left on NESPlay (declared in
// main.js as `_topGamesCache = {};`) since it's plain per-instance cache
// state, not a method — accessed here as app._topGamesCache.

export async function loadTopGamesCard(app) {
  const card = document.getElementById('top-games-card');
  const tabsEl = document.getElementById('top-games-tabs');
  const list = document.getElementById('top-games-list');
  if (!card || !tabsEl || !list) return;

  const systems = app._systemTabList();
  const defaultSys = app.system?.id || 'nes';

  // Render tabs
  tabsEl.innerHTML = '';
  for (const sys of systems) {
    const tab = document.createElement('button');
    tab.className = `top-games-tab${sys === defaultSys ? ' active' : ''}`;
    tab.textContent = TAB_LABELS[sys] || sys.toUpperCase();
    tab.type = 'button';
    tab.addEventListener('click', () => {
      tabsEl.querySelectorAll('.top-games-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      app._renderTopGames(sys, list);
    });
    tabsEl.appendChild(tab);
  }

  card.classList.remove('hidden');
  app._renderTopGames(defaultSys, list);
}

export async function renderTopGames(app, system, list) {
  if (app._topGamesCache[system]) {
    app._renderTopGamesList(app._topGamesCache[system], list, system);
    return;
  }
  list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">Loading...</div>';
  try {
    const res = await fetch(`/api/top-games?system=${system}&limit=10`);
    if (!res.ok) throw new Error();
    const data = await res.json();
    app._topGamesCache[system] = data.games || [];
    app._renderTopGamesList(app._topGamesCache[system], list, system);
  } catch {
    list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">No data</div>';
  }
}

export function renderTopGamesList(app, games, list, system) {
  list.innerHTML = '';
  if (!games.length) {
    list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">No data yet</div>';
    return;
  }
  for (const g of games) {
    const entry = document.createElement('div');
    entry.className = 'top-game-entry';
    const playTime = app._formatDuration(g.total_play_seconds);
    entry.innerHTML =
      `<span class="top-game-rank">${g.rank}</span>` +
      `<span class="top-game-title" title="${app._escapeHtml(g.title)}">${app._escapeHtml(g.title)}</span>` +
      `<span class="top-game-stats">${g.unique_players} players &middot; ${playTime}</span>`;
    if (g.rom_filename) {
      entry.addEventListener('click', () => app._launchTopGame(g.rom_system || system, g));
    }
    list.appendChild(entry);
  }
}

export async function launchTopGame(app, system, game) {
  if (!game.rom_filename) return;

  // Switch system if needed
  if (app.system.id !== system) {
    app._selectSystem(system);
  }

  // Callers pass the catalog title (games.title). It names the game in the
  // load flash and the play-mode prompt until /api/games/lookup answers;
  // a missing or blank title keeps today's ROM-name text.
  const title = typeof game.title === 'string' ? game.title.trim() || null : null;

  // Load the ROM — _selectLibraryRom → _setRom fires the play-mode
  // prompt (Solo / Create Room) just like favorites + recently-played
  // do. Forcing _startSolo() here caused the double state where the
  // game started AND the modal was overlaid on top of it.
  try {
    if (app.system.diskExtensions?.length) {
      await app._selectMultiDiskRom(game.rom_filename, null, { title });
    } else {
      await app._selectLibraryRom(game.rom_filename, null, { title });
    }
  } catch {
    app._flash('Failed to launch game');
  }
}

// ── UP NEXT card (head of My Queue) ─────────────────────────────────
// Shows the top 3 games of the signed-in user's queue with one-click
// Play. The GET lazily creates the queue server-side on first visit.

export async function loadUpNextCard(app) {
  const card = document.getElementById('up-next-card');
  const list = document.getElementById('up-next-list');
  const viewAll = document.getElementById('up-next-view-all');
  if (!card || !list) return;

  if (!authState.isLoaded()) {
    try { await authState.refreshSession(); } catch { /* non-fatal */ }
  }
  if (!authState.getCurrentUser()) {
    card.classList.add('hidden');
    return;
  }

  let queue;
  try {
    const res = await fetch('/api/library/queue', { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    queue = await res.json();
  } catch {
    card.classList.add('hidden');
    return;
  }

  list.innerHTML = '';
  const items = (queue.items || []).slice(0, 3);
  if (!items.length) {
    const emptyEl = document.createElement('div');
    emptyEl.className = 'up-next-empty';
    emptyEl.innerHTML = 'Your queue is empty. Add games from the <a href="/library">library</a>.';
    list.appendChild(emptyEl);
  } else {
    for (const item of items) {
      list.appendChild(app._buildUpNextEntry(item));
    }
  }
  if (viewAll) {
    viewAll.classList.remove('hidden');
    viewAll.href = `/library/list/${encodeURIComponent(queue.id)}`;
  }
  card.classList.remove('hidden');
}

export function buildUpNextEntry(app, item) {
  const entry = document.createElement('div');
  const launchable = !!item.rom_filename;
  entry.className = `up-next-entry${launchable ? '' : ' unlaunchable'}`;

  const cover = document.createElement('img');
  cover.className = 'up-next-cover';
  cover.alt = '';
  cover.loading = 'lazy';
  cover.src = item.game?.cover_url
    ? `/api/library/artwork/cover/${encodeURIComponent(item.game_id)}`
    : '/library/public/placeholder-boxart.svg';
  cover.addEventListener('error', () => {
    cover.src = '/library/public/placeholder-boxart.svg';
  }, { once: true });

  const info = document.createElement('div');
  info.className = 'up-next-info';
  const title = item.game?.title || 'Unknown game';
  const sys = item.rom_system || item.game?.system_id || '';
  info.innerHTML =
    `<span class="up-next-title" title="${app._escapeHtml(title)}">${app._escapeHtml(title)}</span>` +
    (sys ? `<span class="up-next-system">${app._escapeHtml(systemTag(sys))}</span>` : '');

  entry.append(cover, info);

  if (launchable) {
    const playBtn = document.createElement('button');
    playBtn.className = 'up-next-play';
    playBtn.type = 'button';
    playBtn.title = `Play ${title}`;
    playBtn.setAttribute('aria-label', `Play ${title}`);
    playBtn.textContent = '▶';
    playBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      app._launchQueueGame(item);
    });
    entry.appendChild(playBtn);
    entry.addEventListener('click', () => app._launchQueueGame(item));
  }
  return entry;
}

export async function launchQueueGame(app, item) {
  if (!item.rom_filename) return;
  const system = item.rom_system || item.game?.system_id;
  if (system && app.system.id !== system) {
    app._selectSystem(system);
  }
  let loaded = false;
  try {
    if (app.system.diskExtensions?.length) {
      loaded = await app._selectMultiDiskRom(item.rom_filename, null);
    } else {
      loaded = await app._selectLibraryRom(item.rom_filename, null);
    }
  } catch {
    app._flash('Failed to launch game');
    return;
  }
  if (!loaded) return;
  // A queued game is a new game choice; its ROM loader opens the usual mode picker.
  return true;
}

// ── LISTS card (My Lists / Top Lists tabs) ─────────────────────────

export async function loadListsCard(app) {
  const card = document.getElementById('my-lists-card');
  const tabsEl = document.getElementById('lists-tabs');
  const list = document.getElementById('my-lists-list');
  if (!card || !tabsEl || !list) return;
  card.classList.remove('hidden');
  if (!authState.isLoaded()) {
    try { await authState.refreshSession(); } catch { /* non-fatal */ }
  }
  const signedIn = !!authState.getCurrentUser();

  const tabs = [];
  if (signedIn) {
    tabs.push({ key: 'mine', label: 'My Lists' });
  }
  tabs.push({ key: 'top', label: 'Top Lists' });

  // Pre-fetch user's lists to decide the default tab and avoid a
  // double fetch when "My Lists" is the initial view.
  let prefetchedLists = null;
  if (signedIn) {
    try {
      const res = await fetch('/api/library/lists', { credentials: 'include' });
      if (res.ok) prefetchedLists = await res.json();
    } catch { /* fall through to top */ }
  }
  // The auto-created "My Queue" (kind='queue') isn't a curated list — if
  // it's all the user has, the "My Lists" view feels empty, so default to
  // "Top Lists" instead. Only a real custom list keeps "My Lists" default.
  const hasOwnLists = Array.isArray(prefetchedLists)
    && prefetchedLists.some((l) => l.kind !== 'queue');
  const defaultTab = hasOwnLists ? 'mine' : 'top';

  tabsEl.innerHTML = '';
  for (const t of tabs) {
    const btn = document.createElement('button');
    btn.className = `top-games-tab${t.key === defaultTab ? ' active' : ''}`;
    btn.textContent = t.label;
    btn.type = 'button';
    btn.addEventListener('click', () => {
      tabsEl.querySelectorAll('.top-games-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      if (t.key === 'mine') app._renderMyLists(list, card);
      else app._renderTopLists(list, card);
    });
    tabsEl.appendChild(btn);
  }

  if (defaultTab === 'mine') await app._renderMyLists(list, card, prefetchedLists);
  else await app._renderTopLists(list, card);
}

export async function renderMyLists(app, list, card, prefetched) {
  const viewAll = document.getElementById('my-lists-view-all');
  try {
    let lists = prefetched || null;
    if (!lists) {
      const res = await fetch('/api/library/lists', { credentials: 'include' });
      if (!res.ok) { list.innerHTML = ''; return; }
      lists = await res.json();
    }

    list.innerHTML = '';
    if (!lists || !lists.length) {
      list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">No lists yet. Create one in the library.</div>';
    } else {
      for (const item of lists.slice(0, 6)) {
        const row = document.createElement('div');
        row.className = 'my-list-entry';
        const count = Number(item.item_count);
        const visClass = item.visibility === 'private' ? ' private' : '';
        row.innerHTML =
          `<span class="my-list-name">${app._escapeHtml(item.name)}</span>` +
          `<span class="my-list-count">${count} game${count !== 1 ? 's' : ''}</span>` +
          `<span class="my-list-visibility${visClass}">${item.visibility}</span>`;
        row.addEventListener('click', () => {
          window.location.assign(`/library/list/${encodeURIComponent(item.id)}`);
        });
        list.appendChild(row);
      }
    }
    if (viewAll) {
      viewAll.classList.remove('hidden');
      viewAll.onclick = (e) => { e.preventDefault(); window.location.assign('/library/lists'); };
    }
  } catch {
    list.innerHTML = '';
  }
}

export async function renderTopLists(app, list, card) {
  const viewAll = document.getElementById('my-lists-view-all');
  if (viewAll) viewAll.classList.add('hidden');
  try {
    const res = await fetch('/api/library/lists/public?sort=top&limit=6');
    const { lists: items } = res.ok ? await res.json() : { lists: [] };

    list.innerHTML = '';
    if (!items || !items.length) {
      list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">No data yet</div>';
      return;
    }
    for (const item of items) {
      const row = document.createElement('div');
      row.className = 'my-list-entry';
      const curator = app._escapeHtml(item.curator?.display_name || item.curator?.username || '');

      const nameSpan = document.createElement('span');
      nameSpan.className = 'my-list-name';
      nameSpan.textContent = item.name;

      const countSpan = document.createElement('span');
      countSpan.className = 'my-list-count';
      countSpan.textContent = curator;

      row.append(nameSpan, countSpan);

      const likeCount = Number(item.like_count) || 0;
      const heartBtn = document.createElement('button');
      heartBtn.className = 'my-list-heart' + (item.liked_by_me ? ' liked' : '');
      heartBtn.type = 'button';
      const heartCountSpan = document.createElement('span');
      heartCountSpan.className = 'my-list-heart-count';
      heartCountSpan.textContent = likeCount > 0 ? String(likeCount) : '';
      heartBtn.append('♥', heartCountSpan);
      heartBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!authState.getCurrentUser()) return;
        const wasMine = heartBtn.classList.contains('liked');
        const cur = parseInt(heartCountSpan.textContent, 10) || 0;
        const next = wasMine ? Math.max(0, cur - 1) : cur + 1;
        heartBtn.classList.toggle('liked', !wasMine);
        heartCountSpan.textContent = next > 0 ? String(next) : '';
        fetch('/api/reactions', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target_type: 'list', target_id: String(item.id), emoji: 'heart' }),
        }).then(r => { if (!r.ok) throw r; })
          .catch(() => {
            heartBtn.classList.toggle('liked', wasMine);
            heartCountSpan.textContent = likeCount > 0 ? String(likeCount) : '';
          });
      });
      row.appendChild(heartBtn);

      row.addEventListener('click', () => {
        window.location.assign(`/library/list/${encodeURIComponent(item.id)}`);
      });
      list.appendChild(row);
    }
  } catch {
    list.innerHTML = '<div style="padding:8px;font-size:10px;color:var(--text-dim)">No data yet</div>';
  }
}

/**
 * Format a duration in SECONDS as "Xh Ym" or "Ym". Matches the API
 * convention post-0016 migration (play_time_seconds, duration_seconds).
 * Callers with a millisecond-native source (e.g. the local
 * achievement store's playMs) must divide by 1000 before calling.
 */
export function formatDuration(app, seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
